/**
 * @fileoverview Provides the `useAuth` hook for managing user authentication with Firebase. It exposes functions like `signInWithEmail`, `signUpWithEmail`, `signOut`, and `verifyEmail`, and provides access to the current `User` state, loading state, and any errors. The hook utilizes Firebase authentication methods and a cloud function for email verification.
 */

import { useState, useEffect, useCallback } from 'react'
import type { User } from 'firebase/auth'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GithubAuthProvider,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  applyActionCode,
} from 'firebase/auth'
import { auth } from '../firebase'
import { onEmailVerified } from '../firebase/functions'
import { getFirebaseErrorMessage } from '../utils/firebaseErrorMessages'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Subscribe to the Firebase auth state changes
    const unsubscribe = onAuthStateChanged(
      auth,
      (authUser) => {
        setUser(authUser)
        setLoading(false)
      },
      (error) => {
        console.error('Auth state error:', error)
        setError(error.message)
        setLoading(false)
      },
    )

    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [])

  // Sign in with email and password
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, password)
      return true
    } catch (error: any) {
      const errorMessage = getFirebaseErrorMessage(error.code)
      setError(errorMessage)
      return false
    }
  }, [])

  // Sign up with email and password
  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName?: string) => {
      setError(null)
      try {
        const result = await createUserWithEmailAndPassword(auth, email, password)

        // Set display name if provided
        if (displayName && result.user) {
          await updateProfile(result.user, { displayName })
        }

        return true
      } catch (error: any) {
        const errorMessage = getFirebaseErrorMessage(error.code)
        setError(errorMessage)
        return false
      }
    },
    [],
  )

  // Verify email with action code
  const verifyEmail = useCallback(async (actionCode: string) => {
    setError(null)
    try {
      await applyActionCode(auth, actionCode)
      // Force refresh the user to update emailVerified status
      if (auth.currentUser) {
        await auth.currentUser.reload()
        // The onAuthStateChanged listener will automatically update the user state
      }

      // Call the backend function to handle post-verification logic (Mailchimp, etc.)
      try {
        await onEmailVerified({})
        console.log('Email verification processed successfully')
      } catch (verificationError) {
        console.error('Failed to process email verification:', verificationError)
        // Don't fail the verification if the backend processing fails
      }

      return true
    } catch (error: any) {
      setError(error.message)
      return false
    }
  }, [])

  // Sign in with GitHub
  const signInWithGitHub = useCallback(async () => {
    setError(null)
    try {
      const provider = new GithubAuthProvider()
      await signInWithPopup(auth, provider)
      return true
    } catch (error: any) {
      // If GitHub auth is not configured, fail gracefully
      if (error.code === 'auth/configuration-not-found') {
        setError('GitHub authentication is not configured')
      } else {
        const errorMessage = getFirebaseErrorMessage(error.code)
        setError(errorMessage)
      }
      return false
    }
  }, [])

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      return true
    } catch (error: any) {
      const errorMessage = getFirebaseErrorMessage(error.code)
      setError(errorMessage)
      return false
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    setError(null)
    try {
      await sendPasswordResetEmail(auth, email)
      return true
    } catch (error: any) {
      const errorMessage = getFirebaseErrorMessage(error.code)
      setError(errorMessage)
      return false
    }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    setError(null)
    try {
      await firebaseSignOut(auth)
      return true
    } catch (error: any) {
      setError(error.message)
      return false
    }
  }, [])

  return {
    user,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithGitHub,
    signInWithGoogle,
    resetPassword,
    signOut,
    verifyEmail,
  }
}
